import { TPaymentOption } from "apiTypes";
import { createSupportIssue } from "components/supportDataManager";
import useMobile from "customHooks/useMobile";
import { m } from "framer-motion";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import { memo, useContext, useState } from "react";
import { FiHeart } from "react-icons/fi";
import { toast } from "react-toastify";
import styled from "styled-components";
import { NEXT_URL_PROXY } from "../../../config";
import { AuthContext } from "../../../context/AuthContext";
import { AuthModalContext } from "../../../context/AuthModalContext";
import { DonateDetailsContext } from "../../../context/DonateDetailsContext";
import { DonatePageContext } from "../../../context/DonatePageContext";
import { usePreferenceContext } from "../../../context/PreferencesContext";
import { useInfinitePagination } from "../../../customHooks/useInfinitPagination";
import useLike from "../../../customHooks/useLike";
import useModal from "../../../customHooks/useModal";
import { baseReactingApiUrl } from "../../../utils/baseReactingApiUrl";
import FeedbackModal from "../../Donate/FeedbackModal";
import ButtonfulModal, { ButtonfulModalButton } from "../ButtonfulModal";
import MolhamButton from "../MolhamButton";
import ShareBtn from "../ShareBtn";
import {
  CheckoutSteps,
  resetCheckoutStore,
  useCheckoutStore,
} from "./DonateModal/CheckoutState";
import DonateProgressBar from "./DonateProgressBar";
import { SiBitcoincash } from "react-icons/si";

const DonateControlBar = ({ singleCase, noProgressBar, sectionName }) => {
  const { url } = useContext(DonatePageContext);
  const { setCommentsTab, reactingCounts } = useContext(DonateDetailsContext);
  const { handleShowAuthModal, onTabClick } = useContext(AuthModalContext);
  const { donor } = useContext(AuthContext);
  const onSignupBtnClick = () => {
    handleShowAuthModal();
    onTabClick("with_social");
  };

  const onCryptoPaymentClick = () => {
    const baseUrl = "https://www.every.org/molham-volunteering-team";

    const params = new URLSearchParams({
      // require_share_info: "true",
      first_name: donor?.name?.split(" ")[0] || "",
      last_name: donor?.name?.split(" ")[1] || "",
      email: donor?.email || "",
      frequency: "ONCE",
      theme_color: "124d84",
      method: "crypto",
      success_url: sectionName
        ? `https://molhamteam.com/${sectionName}s/${url.id}`
        : "https://molhamteam.com",
      designation: `${sectionName ?? "unknown"}-${url.id}`,
      utm_campaign: "donate-link",
    });

    const cryptoUrl = `${baseUrl}?${params.toString()}#/donate`;
    window.open(cryptoUrl, "_blank");
  };

  const { like, onLikeBtnClick, likeBtnClicked } = useLike(
    singleCase?.counters?.likes,
    baseReactingApiUrl(url, "likes"),
    singleCase?.liked_by_auth,
  );
  const onCommentsBtnClick = () => {
    setCommentsTab(true);
  };
  const setModalVisible = useCheckoutStore((state) => state.setModalVisible);
  const setPaymentOptionData = useCheckoutStore(
    (state) => state.setPaymentOptionData,
  );
  const setPaymentOptions = useCheckoutStore(
    (state) => state.setPaymentOptions,
  );

  const setForceLogin = useCheckoutStore((state) => state.setForceLogin);
  const addStepToHistory = useCheckoutStore((state) => state.addStepToHistory);
  const { formattedData } = useInfinitePagination<TPaymentOption>(
    `${NEXT_URL_PROXY}/targetables/${url.endPoint.split("/")[1]}/${
      url.id
    }/payment-options`,
  );
  const onDonateClick = () => {
    resetCheckoutStore();
    setPaymentOptions(formattedData);
    setPaymentOptionData({
      targetableId: url.id,
      targetableType: url.endPoint.split("/")[1],
      payment_options_label: singleCase?.payment_options_label,
    });
    addStepToHistory(CheckoutSteps.PaymentOptionStep.name);
    if (singleCase?.checkout_eligible_for_guest === false) {
      setForceLogin(true);
    } else {
      setForceLogin(false);
    }
    setModalVisible(true);
  };
  const { curr } = usePreferenceContext();
  const isMobile = useMobile("lg");
  const { showModal, handleCloseModal, handleShowModal } = useModal();
  const {
    showModal: showFBModal,
    handleCloseModal: handleCloseFBModal,
    handleShowModal: handleShowFBModal,
  } = useModal();
  const { t } = useTranslation("common");
  const buttons: ButtonfulModalButton = [
    [
      {
        text: t("single_targetable-send_feedback"),
        btnFntSz: "fs-lg",
        onClick: () => {
          handleCloseModal();
          handleShowFBModal();
        },
        color: "primary",
      },
      {
        text: t(`single_targetable-chat`),
        onClick: () => {
          handleCloseModal();
        },
        color: "primary",
        href: "#chat-with-support",
      },
    ],
    [
      {
        text: t(`cancel`),
        onClick: () => handleCloseModal(),
        color: "primary",
        fontWeight: "bold",
      },
    ],
  ];
  const buttonsNoAuth: ButtonfulModalButton = [
    [
      {
        text: t(`single_targetable-chat`),
        onClick: () => {
          handleCloseModal();
        },
        color: "primary",
        href: "#chat-with-support",
      },
    ],
    [
      {
        text: t(`cancel`),
        onClick: () => handleCloseModal(),
        color: "primary",
        fontWeight: "bold",
      },
    ],
  ];
  const [isLoading, setIsLoading] = useState(false);
  const { push } = useRouter();
  const onCreate = async (d) => {
    setIsLoading(true);
    const res = await createSupportIssue({
      content: d.content,
      subject: d.subject,
    });
    if (res.status === 200) {
      handleCloseFBModal();
      toast.success(t("issue_created"));
      push("/profile/support-issues");
    } else {
      toast.error(t("something_went_wrong"));
    }
    setIsLoading(false);
  };
  return (
    <>
      <ButtonfulModal
        handleCloseModal={handleCloseModal}
        showModal={showModal}
        buttons={donor ? buttons : buttonsNoAuth}
      />
      <FeedbackModal
        closeModalOffCanvas={handleCloseFBModal}
        showModalOffcanvas={showFBModal}
        onSubmit={onCreate}
        isLoading={isLoading}
        isAdd
      />

      {noProgressBar ? null : (
        <div className="d-block d-lg-none py-3">
          <DonateProgressBar
            progressPrecentage={singleCase?.funding_progress_bar?.percentage}
            received={
              singleCase?.funding_progress_bar?.blocks[0]?.amount?.[curr?.code]
            }
            left={
              singleCase?.funding_progress_bar?.blocks[1]?.amount?.[curr?.code]
            }
            blocks={singleCase?.funding_progress_bar?.blocks}
          />
        </div>
      )}
      <DonateControlBarWrapper
        className={`d-flex align-items-center mt-0 mb-3 mb-lg-4 mt-lg-4`}
      >
        <m.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`d-flex flex-wrap align-items-center flex-2 ${
            isMobile && "w-100"
          }`}
        >
          <MolhamButton
            id="donate-btn"
            startIcon={singleCase?.funded ? "fe-smile" : "fe-heart"}
            label={singleCase?.donate_button_label}
            className={`text-capitalize  fs-lg donation-btn fw-bold px-5 mb-3 mb-lg-0 `}
            onClick={onDonateClick}
          />

          <MolhamButton
            id="crypto-donate-btn"
            CustomIconButton={<SiBitcoincash size={26} />}
            variant={null}
            className={`btn-translucent-primary fs-lg px-3 mx-2 mb-lg-0`}
            onClick={onCryptoPaymentClick}
          />

          <>
            <ShareBtn
              auth_share_url={
                singleCase?.auth_share_url || singleCase?.guest_share_url
              }
              title={singleCase?.contents?.title}
              apiURL={baseReactingApiUrl(url, "shares")}
            />

            <MolhamButton
              id="t9-like-btn"
              CustomIconButton={
                <FiHeart
                  className="like-btn fs-lg"
                  size={18}
                  fill={likeBtnClicked ? "#f74f78" : "rgba(247, 79, 120, 0.08)"}
                />
              }
              label={like > 0 && like.toString()}
              variant={null}
              className={`text-capitalize btn-translucent-danger fs-lg px-3`}
              onClick={onLikeBtnClick}
            />
            <MolhamButton
              id="t9-comment-btn"
              startIcon="fe-message-circle"
              label={
                reactingCounts.comments_count > 0 &&
                reactingCounts.comments_count.toString()
              }
              variant={null}
              className={`text-capitalize bg-faded-info fs-lg  ml-2 mr-md-2 mr-0 px-3`}
              onClick={onCommentsBtnClick}
            />
            <MolhamButton
              id="t9-more-btn"
              startIcon="fe-more-horizontal fs-xxl"
              className="px-2"
              variant={null}
              onClick={handleShowModal}
            />
          </>
        </m.div>

        {noProgressBar ? null : (
          <div className={`d-none d-lg-block  flex-1 `}>
            <DonateProgressBar
              progressPrecentage={singleCase?.funding_progress_bar?.percentage}
              received={
                singleCase?.funding_progress_bar?.blocks[0]?.amount?.[
                  curr?.code
                ]
              }
              left={
                singleCase?.funding_progress_bar?.blocks[1]?.amount?.[
                  curr?.code
                ]
              }
              blocks={singleCase?.funding_progress_bar?.blocks}
            />
          </div>
        )}
      </DonateControlBarWrapper>
    </>
  );
};

export default memo(DonateControlBar);
const DonateControlBarWrapper = styled.div`
  .dropdown-toggle:not(.dropdown-toggle-split)::after {
    display: none;
  }
  .donation-btn {
    padding-left: 30px;
    padding-right: 30px;
  }
  @media (max-width: 991.98px) {
    .donation-btn {
      display: block;
      width: 100%;
    }
  }
  .input-group > .form-control {
    flex: 2;
  }

  .btn-translucent-danger:hover,
  .btn-translucent-danger.active {
    background-color: rgba(247, 79, 120, 0.08);
    color: #f74f78 !important;
  }
`;
